import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { ActionIcon, Menu } from "@mantine/core"
import {
  IconDotsVertical,
  IconEye,
  IconEyeOff,
  IconFileDescription,
  IconRefresh,
  IconTrash,
  IconUserPlus,
} from "@tabler/icons-react"
import type { IProps, TMeetingCardAction } from "./interfaces"

/**
 * MeetingCardKebab — top-right "more actions" menu on a meeting card.
 *
 * Dumb component. Knows the menu layout + which items render under which
 * permissions, but owns no state and no API calls. Click a menu item →
 * `onAction(...)` fires with a discriminated `TMeetingCardAction`. The
 * host (meeting list page) decides what to do per action: navigate,
 * open a modal, confirm + mutate, etc.
 *
 * The trigger swallows clicks/keystrokes so the wrapping `<Link>` on the
 * card doesn't fire when the user reaches for the menu. Menu items render
 * in a portal and are outside the link tree, so their own clicks are
 * unaffected.
 */
export const MeetingCardKebab = ({
  hidden = false,
  canManage,
  canReprocess,
  canDeletePermanently,
  onAction,
  variant = "full",
}: IProps): React.JSX.Element | null => {
  const { i18n } = useLingui()
  const isLite = variant === "lite"

  // Lite hides transcript + reprocess; a viewer with no manage rights would
  // otherwise get an empty menu.
  if (isLite && !canManage) {
    return null
  }

  // Prevent the parent <Link> from firing when the user reaches for the kebab.
  // pointerdown is the safer hook than click — Mantine's Menu trigger opens
  // on pointerdown, so by the time `click` would bubble the menu is already
  // open and the navigation would still fire.
  const swallow = (e: React.SyntheticEvent): void => {
    e.preventDefault()
    e.stopPropagation()
  }

  const pick =
    (action: TMeetingCardAction) =>
    (e: React.MouseEvent): void => {
      e.preventDefault()
      e.stopPropagation()
      onAction(action)
    }

  return (
    <Menu shadow="md" width={220} position="bottom-end" withinPortal>
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          size="md"
          aria-label={i18n._(msg`More actions`)}
          data-testid="meeting-card-more"
          onPointerDown={swallow}
          onClick={swallow}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") swallow(e)
          }}
        >
          <IconDotsVertical size={16} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {!isLite && (
          <Menu.Item leftSection={<IconFileDescription size={14} />} onClick={pick("transcript")}>
            <Trans>View transcript</Trans>
          </Menu.Item>
        )}
        {canManage && canReprocess && (
          <Menu.Item leftSection={<IconRefresh size={14} />} onClick={pick("reprocess")}>
            <Trans>Reprocess</Trans>
          </Menu.Item>
        )}
        {canManage && (
          <Menu.Item leftSection={<IconUserPlus size={14} />} onClick={pick("access")}>
            <Trans>Manage access</Trans>
          </Menu.Item>
        )}
        {canManage && (
          <Menu.Item
            leftSection={hidden ? <IconEye size={14} /> : <IconEyeOff size={14} />}
            onClick={pick("toggle-hidden")}
          >
            {hidden ? <Trans>Unhide</Trans> : <Trans>Hide</Trans>}
          </Menu.Item>
        )}
        {canManage && (
          <>
            <Menu.Divider />
            <Menu.Item leftSection={<IconTrash size={14} />} color="danger" onClick={pick("delete")}>
              {canDeletePermanently ? <Trans>Delete permanently</Trans> : <Trans>Delete</Trans>}
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  )
}
